/* eslint-disable no-nested-ternary */
import {
  ActivityHeader,
  Breadcrumbs,
  Icon,
  ModuleActionBar,
  ProgressBar,
  ReportModalLayout,
  TestAnswerList,
  TestCardsFrame,
} from '@components';
import {
  useNavigateWithOfflineMode,
  useScreenSize,
  useScrollToTop,
} from '@hooks';
import Net from '@pngs/TestPage/Net10.png';
import { testApi } from '@store';
import { Button, Flex, theme, Typography } from '@styles';
import { ReactComponent as EyeIcon } from '@svgs/visibility.svg';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useOnlineStatusDetector } from '../../hooks/useOnlineStatusDetector';
import { Container, testPageLayoutByVariant } from './TestPage.layoutProps';

const TestPage = () => {
  const { navigate } = useNavigateWithOfflineMode();
  const { i18n } = useTranslation();
  const { state } = useLocation();
  const { device, isLaptop, isTabletSM } = useScreenSize();
  const layout = testPageLayoutByVariant[device || 'desktop'];
  const { isOnline } = useOnlineStatusDetector();

  const [shouldShowTitleCard, setShouldShowTitleCard] = useState(true);
  const [areAnswersShown, setAreAnswersShown] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isTestPassed, setIsTestPassed] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [validatedAnswers, setValidatedAnswers] = useState(null);
  const [isReportModalVisible, setReportModalVisible] = useState(false);
  const [isValidated, setIsValidated] = useState(false);

  const [validateTest] = testApi.useValidateTestByIdMutation();

  const { refetch: refetchTest, test } = testApi.useGetTestByIdQuery(
    { id: state?.exerciseId },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data }) => ({
        test: data?.data,
      }),
      skip: !state?.exerciseId || !isOnline,
    }
  );

  useEffect(() => {
    if (state?.exerciseId) {
      refetchTest({ id: state.exerciseId });
    }
  }, [i18n.language]);

  const isTestFinished = currentStep === test?.total_steps;

  const handleBeginTest = () => {
    setShouldShowTitleCard(false);
  };

  const handleShowAnswers = () => {
    setAreAnswersShown(true);
  };

  const handleAnswer = (id, option) => () => {
    setAnswers([...answers, { option, testStepId: id }]);
    setCurrentStep(currentStep + 1);
  };

  // eslint-disable-next-line no-unused-vars
  const handleValidateTest = async () => {
    const { data } = await validateTest({
      data: {
        testAnswers: answers,
      },
      id: state?.exerciseId,
    }).unwrap();
    setIsTestPassed(data?.is_passed);
    setCorrectAnswers(data?.correct_answers);
    setValidatedAnswers(data?.validated_answers);
    setTimeout(() => {
      setIsValidated(true);
    }, 200);
  };

  const handleResetTest = () => {
    setAnswers([]);
    setCurrentStep(0);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleReportButtonClick = () => {
    setReportModalVisible(true);
  };

  useScrollToTop();

  useEffect(() => {
    if (isTestFinished) {
      // todo: add test answers in db and fix request params to send optionId instead of option
      // handleValidateTest();
    }
  }, [currentStep]);

  return (
    <Container
      backgroundImage={Net}
      backgroundRepeat="no-repeat"
      backgroundSize="cover"
      flexDirection="column"
    >
      <ReportModalLayout
        activityId={state?.exerciseId}
        isLessonActivity
        isVisible={isReportModalVisible}
        setIsVisible={setReportModalVisible}
        withBackdropNoise
      />
      <Breadcrumbs containerProps={{ px: layout?.px }} />
      {shouldShowTitleCard ? (
        <Flex
          alignItems="center"
          flexDirection={isTabletSM ? 'column' : 'row'}
          gap={8}
          justifyContent="space-between"
          pb={80}
          pl={layout?.pl}
          pr={isTabletSM ? layout?.pl : 0}
          pt={isTabletSM ? 0 : 80}
        >
          {isTabletSM && (
            <Flex
              backgroundImage={test?.image}
              // eslint-disable-next-line no-nested-ternary
              height={isTabletSM ? 248 : isLaptop ? 337 : 470}
              maxWidth={isLaptop ? 523 : 650}
              width="100%"
            />
          )}
          <Flex
            alignItems="flex-start"
            flex={1}
            flexDirection="column"
            mr={isTabletSM ? 0 : 40}
            {...(isTabletSM && { width: '100%' })}
          >
            <Typography
              color={theme.colors.white[50]}
              mb={isTabletSM ? '8px' : 32}
              variant="BN-bold-s43-lh38"
            >
              {i18n.t('TestPage.Test').toUpperCase()}
            </Typography>
            <Typography
              color={theme.colors.white[100]}
              maxWidth={566}
              mb={10}
              variant={isTabletSM ? 'BN-bold-s32-lh28' : 'BN-bold-s64-lh64'}
              width="100%"
            >
              {test?.title?.toUpperCase()}
            </Typography>
            {test?.views && (
              <Flex alignItems="center" gap={8} mb={30}>
                <Icon color={theme.colors.alto[100]} SVG={EyeIcon} />
                <Typography
                  color={theme.colors.alto[100]}
                  variant="MS-regular-s14-lh22"
                >
                  {test?.views}
                </Typography>
              </Flex>
            )}
            <Button
              onClick={handleBeginTest}
              variant="primary"
              whiteSpace="nowrap"
              width={340}
            >
              <Typography
                color={theme.colors.black[100]}
                variant="MS-semibold-s16-lh17"
              >
                {i18n.t('TestPage.BeginTest').toUpperCase()}
              </Typography>
            </Button>
          </Flex>
          {!isTabletSM && (
            <Flex
              backgroundImage={test?.image}
              height={isLaptop ? 337 : 470}
              maxWidth={isLaptop ? 523 : 650}
              width="100%"
            />
          )}
        </Flex>
      ) : (
        <Flex flexDirection="column" px={layout?.px}>
          <ModuleActionBar
            additionalButton={{ isVisible: false }}
            progress={{ isSemivisible: true, percentage: 0 }}
            report={{
              isVisible: true,
              onClick: handleReportButtonClick,
              variant: 'report',
            }}
            sidePanel={{ isSemivisible: true }}
          />
          <ActivityHeader
            activityVariant="test"
            containerProps={{ mb: 20, mt: 24 }}
            title={test?.title}
          />
          <ProgressBar
            currentStep={currentStep}
            items={isTestFinished ? validatedAnswers : test?.steps}
            withCorrection={isTestFinished}
          />
          {areAnswersShown ? (
            <TestAnswerList validatedAnswers={validatedAnswers} />
          ) : (
            <TestCardsFrame
              correctAnswers={correctAnswers} // TO DO validation
              data={test?.steps}
              handleGoBack={handleGoBack}
              handleReset={handleResetTest}
              handleShowAnswers={handleShowAnswers}
              isTestFinished={isTestFinished}
              isTestPassed={isTestPassed}
              isValidated={isValidated}
              onLeftButtonPress={handleAnswer(
                test?.steps?.[currentStep]?.id,
                false
              )}
              onRightButtonPress={handleAnswer(
                test?.steps?.[currentStep]?.id,
                true
              )}
            />
          )}
        </Flex>
      )}
    </Container>
  );
};

export default TestPage;
